import { Link } from "react-router-dom";
import { ArrowUpRight, Check, HardHat, Paintbrush, SquareStack } from "lucide-react";
import { FadeUp, KineticLines, Overline } from "@/components/Reveal";
import { SERVICES } from "@/lib/data";

const ICONS = [HardHat, Paintbrush, SquareStack];

const Services = () => (
  <main className="pt-20">
    <section data-testid="services-hero" className="relative overflow-hidden border-b border-white/10">
      <div className="pointer-events-none absolute inset-0 blueprint-grid opacity-50" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1600px] px-5 pb-16 pt-20 sm:px-8 lg:px-12 lg:pb-24 lg:pt-28">
        <FadeUp>
          <Overline>Site Services — Supply & Execution</Overline>
        </FadeUp>
        <KineticLines
          className="mt-8"
          delay={0.2}
          lineClassName="font-display uppercase leading-[0.92] tracking-tight text-slate-50 text-[clamp(2.6rem,8vw,7.5rem)]"
          lines={[<>Beyond fabrication.</>, <><span className="text-outline-orange">On your site.</span></>]}
        />
        <FadeUp delay={0.5} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-slate-300">
            Alongside FRP manufacturing, our crews execute work where it matters —
            inside your plant. Scaffolding, industrial painting and epoxy systems,
            delivered with the same engineering discipline.
          </p>
        </FadeUp>
      </div>
    </section>

    <section data-testid="services-list" className="mx-auto max-w-[1600px] px-5 py-16 sm:px-8 lg:px-12 lg:py-24">
      <div className="flex flex-col gap-20 lg:gap-28">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[i];
          const flip = i % 2 === 1;
          return (
            <FadeUp key={s.id}>
              <article
                data-testid={`service-${s.id}`}
                className={`grid items-stretch gap-px border border-white/10 bg-white/10 lg:grid-cols-2`}
              >
                <div className={`relative min-h-[300px] overflow-hidden bg-slate-950 ${flip ? "lg:order-2" : ""}`}>
                  <img
                    src={s.image}
                    alt={s.name}
                    className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30" />
                  <span className="absolute bottom-4 left-4 bg-slate-950/80 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-orange-500 backdrop-blur">
                    Fig. S{s.num}
                  </span>
                </div>

                <div className={`bg-slate-950 p-8 lg:p-12 ${flip ? "lg:order-1" : ""}`}>
                  <div className="flex items-start justify-between">
                    <span className="text-outline-orange font-display text-6xl leading-none lg:text-7xl">
                      S{s.num}
                    </span>
                    <span className="flex h-12 w-12 items-center justify-center border border-orange-600/50">
                      <Icon className="h-5 w-5 text-orange-500" />
                    </span>
                  </div>
                  <h2 className="mt-10 font-display text-3xl uppercase leading-tight tracking-wide text-slate-50 lg:text-4xl">
                    {s.name}
                  </h2>
                  <span className="mt-3 inline-block border border-white/15 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-400">
                    {s.tag}
                  </span>
                  <p className="mt-6 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {s.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`service-${s.id}-cta`}
                    className="group mt-10 inline-flex items-center gap-2 border border-orange-600 px-6 py-3.5 font-mono text-xs tracking-[0.25em] uppercase text-orange-500 transition-colors duration-300 hover:bg-orange-600 hover:text-slate-950"
                  >
                    Enquire About This Service
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </article>
            </FadeUp>
          );
        })}
      </div>

      <FadeUp className="mt-20 border border-white/10 bg-slate-900/40 p-8 lg:p-10">
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div>
            <h3 className="font-display text-2xl uppercase text-slate-50 lg:text-3xl">
              One crew. Products plus execution.
            </h3>
            <p className="mt-2 max-w-lg text-sm text-slate-400">
              Combine FRP supply with scaffolding, painting or epoxy work in a
              single work order — one vendor, one schedule, one bill.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="group inline-flex items-center gap-2 bg-orange-600 px-7 py-4 font-mono text-xs tracking-[0.25em] uppercase text-slate-950 transition-colors duration-300 hover:bg-orange-500"
          >
            Get a Combined Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </FadeUp>
    </section>
  </main>
);

export default Services;
